import { useState } from "react"
import { Button } from "@/modules/shared/ui/components/button"
import { Label } from "@/modules/shared/ui/components/label"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import { useEligibleAssignees } from "../../hooks/useEligibleAssignees"
import { AssignResourceAccessRequest, ResourceAccessLevel } from "../../dtos/ResourceAccessDtos"

interface AssignAccessFormProps {
  onAssign: (userId: string, request: AssignResourceAccessRequest) => void
  isPending: boolean
  alreadyAssignedUserIds: string[]
}

export function AssignAccessForm({
  onAssign,
  isPending,
  alreadyAssignedUserIds,
}: AssignAccessFormProps) {
  const { eligible, isLoading } = useEligibleAssignees()

  const [emailFilter, setEmailFilter] = useState("")
  const [selectedUserId, setSelectedUserId] = useState("")
  const [level, setLevel] = useState<ResourceAccessLevel>("Read")
  const [validationError, setValidationError] = useState("")

  const filteredUsers = eligible.filter(
    (u) =>
      !emailFilter ||
      u.email.toLowerCase().includes(emailFilter.toLowerCase()) ||
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(emailFilter.toLowerCase()),
  )

  const handleSubmit = () => {
    setValidationError("")

    if (!selectedUserId) {
      setValidationError("Please select a team member.")
      return
    }

    onAssign(selectedUserId, { level })

    setSelectedUserId("")
    setEmailFilter("")
    setLevel("Read")
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Search team member by name or email</Label>
        <Input
          placeholder="Filter by email or name…"
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
        />
        <Select value={selectedUserId} onValueChange={setSelectedUserId} disabled={isLoading}>
          <SelectTrigger>
            <SelectValue placeholder={isLoading ? "Loading…" : "Select a team member"} />
          </SelectTrigger>
          <SelectContent>
            {filteredUsers.length === 0 ? (
              <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                No eligible team members found
              </div>
            ) : (
              filteredUsers.map((u) => (
                <SelectItem
                  key={u.id}
                  value={u.id}
                  disabled={alreadyAssignedUserIds.includes(u.id)}
                >
                  {u.firstName} {u.lastName} ({u.email})
                  {alreadyAssignedUserIds.includes(u.id) ? " — already assigned" : ""}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Access level</Label>
        <Select value={level} onValueChange={(v) => setLevel(v as ResourceAccessLevel)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Read">Read — can view only</SelectItem>
            <SelectItem value="Edit">Edit — can view and make changes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {validationError && <p className="text-sm text-destructive">{validationError}</p>}

      <Button onClick={handleSubmit} disabled={isPending} className="w-full">
        {isPending ? "Assigning…" : "Assign access"}
      </Button>
    </div>
  )
}
